import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function Sliders() {

    /*
    |--------------------------------------------------------------------------
    | State
    |--------------------------------------------------------------------------
    */

    const [sliders, setSliders] = useState([]);

    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 10;


    /*
    |--------------------------------------------------------------------------
    | Fetch Sliders
    |--------------------------------------------------------------------------
    */

    const fetchSliders = async () => {

        try {

            setLoading(true);


            const response = await fetch(
                `${API_BASE_URL}/admin/sliders`,
                {
                    method: "GET",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include"
                }
            );


            const data = await response.json();


            if (response.ok && data.success) {

                setSliders(data.sliders || []);

            } else {

                Swal.fire({

                    icon: "error",

                    title: "Unable to Load Sliders",

                    text:
                        data.message ||
                        "Unable to fetch sliders."

                });
            }

        } catch (error) {

            console.error(
                "Fetch Sliders Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Unable to connect with server."

            });

        } finally {

            setLoading(false);

        }
    };


    /*
    |--------------------------------------------------------------------------
    | Load Sliders
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchSliders();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Delete Slider
    |--------------------------------------------------------------------------
    */

    const handleDelete = async (id) => {

        const result = await Swal.fire({

            icon: "warning",

            title: "Are you sure?",

            text:
                "This slider will be deleted permanently.",

            showCancelButton: true,

            confirmButtonColor: "#d33",

            cancelButtonColor: "#6c757d",

            confirmButtonText:
                "Yes, Delete",

            cancelButtonText:
                "Cancel"

        });


        if (!result.isConfirmed) {

            return;

        }


        try {

            const response = await fetch(

                `${API_BASE_URL}/admin/sliders/${id}`,

                {

                    method: "DELETE",

                    headers: {

                        "Accept":
                            "application/json"

                    },

                    credentials:
                        "include"

                }
            );


            const data =
                await response.json();


            if (
                response.ok &&
                data.success
            ) {

                setSliders(
                    (previousSliders) =>
                        previousSliders.filter(
                            (item) =>
                                item.id !== id
                        )
                );


                await Swal.fire({

                    icon: "success",

                    title: "Deleted!",

                    text:
                        data.message ||
                        "Slider deleted successfully.",

                    timer: 1500,

                    showConfirmButton:
                        false

                });

            } else {

                Swal.fire({

                    icon: "error",

                    title: "Delete Failed",

                    text:
                        data.message ||
                        "Unable to delete slider."

                });
            }

        } catch (error) {

            console.error(
                "Delete Slider Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Unable to connect with server."

            });
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    const filteredSliders =
        sliders.filter((item) => {

            const searchText =
                search
                    .toLowerCase()
                    .trim();


            return (

                String(item.id || "")
                    .toLowerCase()
                    .includes(searchText)

                ||

                String(item.image || "")
                    .toLowerCase()
                    .includes(searchText)

                ||

                String(item.badge || "")
                    .toLowerCase()
                    .includes(searchText)

                ||

                String(item.title || "")
                    .toLowerCase()
                    .includes(searchText)

                ||

                String(item.description || "")
                    .toLowerCase()
                    .includes(searchText)

                ||

                String(item.order || "")
                    .toLowerCase()
                    .includes(searchText)

            );

        });


    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const totalPages =
        Math.ceil(
            filteredSliders.length /
            itemsPerPage
        );


    const startIndex =
        (currentPage - 1) *
        itemsPerPage;


    const endIndex =
        startIndex +
        itemsPerPage;


    const currentSliders =
        filteredSliders.slice(
            startIndex,
            endIndex
        );


    /*
    |--------------------------------------------------------------------------
    | Reset Page When Search Changes
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        setCurrentPage(1);

    }, [search]);


    /*
    |--------------------------------------------------------------------------
    | Correct Current Page
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (
            totalPages > 0 &&
            currentPage > totalPages
        ) {

            setCurrentPage(totalPages);

        }

    }, [
        currentPage,
        totalPages
    ]);


    /*
    |--------------------------------------------------------------------------
    | Image URL
    |--------------------------------------------------------------------------
    */

    const getImageUrl = (image) => {

        if (!image) {

            return "";

        }


        const baseUrl =
            API_BASE_URL.replace(
                "/api",
                ""
            );


        return `${baseUrl}/storage/${image}`;

    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="container-fluid">

                <div className="text-center py-5">

                    <div
                        className="spinner-border text-primary"
                        role="status"
                    >
                    </div>


                    <p className="mt-2">
                        Loading sliders...
                    </p>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid">

            {/* -------------------------------------------------------------- */}
            {/* Header */}
            {/* -------------------------------------------------------------- */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <h3 className="mb-0">
                    Sliders
                </h3>


                <Link
                    to="/add-slider"
                    className="btn btn-primary"
                >
                    + Add Slider
                </Link>

            </div>


            {/* -------------------------------------------------------------- */}
            {/* Search */}
            {/* -------------------------------------------------------------- */}

            <div className="card shadow-sm mb-3">

                <div className="card-body">

                    <input
                        type="text"
                        className="form-control"
                        placeholder="
                            Search by ID, badge, title,
                            description, image or order...
                        "
                        value={search}
                        onChange={(e) =>
                            setSearch(e.target.value)
                        }
                    />

                </div>

            </div>


            {/* -------------------------------------------------------------- */}
            {/* Table */}
            {/* -------------------------------------------------------------- */}

            <div className="card shadow-sm">

                <div
                    className="card-body p-0"
                    style={{
                        maxHeight: "550px",
                        overflowX: "auto",
                        overflowY: "auto"
                    }}
                >

                    <table
                        className="
                            table
                            table-bordered
                            table-hover
                            mb-0
                            align-middle
                        "
                    >

                        <thead className="table-light">

                            <tr>

                                <th
                                    style={{
                                        width: "60px"
                                    }}
                                >
                                    #
                                </th>


                                <th
                                    style={{
                                        width: "200px"
                                    }}
                                >
                                    Image
                                </th>


                                <th
                                    style={{
                                        width: "180px"
                                    }}
                                >
                                    Badge
                                </th>


                                <th
                                    style={{
                                        minWidth: "250px"
                                    }}
                                >
                                    Title
                                </th>


                                <th
                                    style={{
                                        minWidth: "350px"
                                    }}
                                >
                                    Description
                                </th>


                                <th
                                    style={{
                                        width: "90px"
                                    }}
                                >
                                    Order
                                </th>


                                <th
                                    style={{
                                        width: "180px"
                                    }}
                                >
                                    Action
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {currentSliders.length > 0 ? (

                                currentSliders.map(
                                    (item, index) => (

                                        <tr
                                            key={item.id}
                                        >

                                            {/* ------------------------------------------------ */}
                                            {/* Number */}
                                            {/* ------------------------------------------------ */}

                                            <td>

                                                {startIndex +
                                                    index +
                                                    1}

                                            </td>


                                            {/* ------------------------------------------------ */}
                                            {/* Image */}
                                            {/* ------------------------------------------------ */}

                                            <td>

                                                {item.image ? (

                                                    <img
                                                        src={getImageUrl(
                                                            item.image
                                                        )}
                                                        alt={
                                                            item.title ||
                                                            "Slider"
                                                        }
                                                        style={{
                                                            width:
                                                                "180px",

                                                            height:
                                                                "75px",

                                                            objectFit:
                                                                "cover",

                                                            borderRadius:
                                                                "5px",

                                                            display:
                                                                "block"
                                                        }}
                                                    />

                                                ) : (

                                                    <span className="text-muted">

                                                        No Image

                                                    </span>

                                                )}

                                            </td>


                                            {/* ------------------------------------------------ */}
                                            {/* Badge */}
                                            {/* ------------------------------------------------ */}

                                            <td>

                                                {item.badge ? (

                                                    <span
                                                        className="
                                                            badge
                                                            bg-primary
                                                        "
                                                    >
                                                        {item.badge}
                                                    </span>

                                                ) : (

                                                    <span className="text-muted">
                                                        —
                                                    </span>

                                                )}

                                            </td>


                                            {/* ------------------------------------------------ */}
                                            {/* Title */}
                                            {/* ------------------------------------------------ */}

                                            <td>

                                                <div
                                                    style={{
                                                        fontWeight:
                                                            "600"
                                                    }}
                                                >
                                                    {item.title ||
                                                        "—"}
                                                </div>

                                            </td>


                                            {/* ------------------------------------------------ */}
                                            {/* Description */}
                                            {/* ------------------------------------------------ */}

                                            <td>

                                                <div
                                                    style={{
                                                        maxWidth:
                                                            "400px",

                                                        whiteSpace:
                                                            "normal",

                                                        wordBreak:
                                                            "break-word"
                                                    }}
                                                >

                                                    {item.description ||
                                                        "—"}

                                                </div>

                                            </td>


                                            {/* ------------------------------------------------ */}
                                            {/* Order */}
                                            {/* ------------------------------------------------ */}

                                            <td>

                                                {item.order}

                                            </td>


                                            {/* ------------------------------------------------ */}
                                            {/* Actions */}
                                            {/* ------------------------------------------------ */}

                                            <td>

                                                <Link
                                                    to={`/edit-slider/${item.id}`}
                                                    className="
                                                        btn
                                                        btn-sm
                                                        btn-warning
                                                        me-2
                                                    "
                                                >
                                                    Edit
                                                </Link>


                                                <button
                                                    type="button"
                                                    className="
                                                        btn
                                                        btn-sm
                                                        btn-danger
                                                    "
                                                    onClick={() =>
                                                        handleDelete(
                                                            item.id
                                                        )
                                                    }
                                                >
                                                    Delete
                                                </button>

                                            </td>

                                        </tr>

                                    )
                                )

                            ) : (

                                <tr>

                                    <td
                                        colSpan="7"
                                        className="
                                            text-center
                                            py-4
                                        "
                                    >
                                        No sliders found.
                                    </td>

                                </tr>

                            )}

                        </tbody>

                    </table>

                </div>


                {/* ---------------------------------------------------------- */}
                {/* Footer */}
                {/* ---------------------------------------------------------- */}

                <div className="card-footer">

                    <div
                        className="
                            d-flex
                            justify-content-between
                            align-items-center
                            flex-wrap
                            gap-2
                        "
                    >

                        {/* -------------------------------------------------- */}
                        {/* Showing */}
                        {/* -------------------------------------------------- */}

                        <div>

                            Showing{" "}

                            {filteredSliders.length > 0
                                ? startIndex + 1
                                : 0}

                            {" "}to{" "}

                            {Math.min(
                                endIndex,
                                filteredSliders.length
                            )}

                            {" "}of{" "}

                            {filteredSliders.length}

                            {" "}sliders

                        </div>


                        {/* -------------------------------------------------- */}
                        {/* Pagination */}
                        {/* -------------------------------------------------- */}

                        <div>

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-sm
                                    btn-secondary
                                    me-2
                                "
                                disabled={
                                    currentPage === 1 ||
                                    totalPages === 0
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        (page) =>
                                            page - 1
                                    )
                                }
                            >
                                Previous
                            </button>


                            <span className="me-2">

                                Page{" "}

                                {totalPages > 0
                                    ? currentPage
                                    : 0}

                                {" "}of{" "}

                                {totalPages}

                            </span>


                            <button
                                type="button"
                                className="
                                    btn
                                    btn-sm
                                    btn-secondary
                                "
                                disabled={
                                    currentPage ===
                                        totalPages ||
                                    totalPages === 0
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        (page) =>
                                            page + 1
                                    )
                                }
                            >
                                Next
                            </button>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

}


export default Sliders;